import { checkoutUrlBuilder } from '@ps/features/checkout/builders';
import type { GuestContact } from '@ps/features/checkout/types/GuestContact';

import type { MeetingSlot } from '../../../common/types/MeetingSlot';
import type { MeetingType } from '../../../common/types/MeetingType';

/**
 * Build the `/checkout?…` URL that books a meeting slot, reusing the shared
 * `checkoutUrlBuilder()` (single source of truth for the checkout param
 * contract) — the Next mirror of RN's `useStartMeetingCheckout`. Free and paid
 * meetings use the SAME URL: `MeetingCheckoutEngine` handles a `price === 0`
 * booking as a Stripe setup (no charge), so no free-vs-paid branch is needed
 * here.
 *
 * A URL coupon is forwarded verbatim (RN carries the display coupon into the
 * purchase; the meeting order engine — `AbstractSellerOrderCheckoutEngine` —
 * redeems `coupon` server-side, matching the PDP's `buildListingCheckoutUrl`).
 */
export function buildMeetingCheckoutUrl(params: {
  /** The creator's public `url_code` (the seller-backed checkout target). */
  urlCode: string;
  meetingType: MeetingType;
  slot: MeetingSlot;
  /** IANA timezone the slot is expressed in (e.g. `America/New_York`). */
  timezone: string;
  /** Optional URL coupon to carry into the booking. */
  coupon?: string;
  guestContact?: GuestContact;
}): string {
  const { urlCode, meetingType, slot, timezone, coupon, guestContact } = params;

  const builder = checkoutUrlBuilder().meeting(intent => {
    intent
      .urlCode(urlCode)
      .meetingType(meetingType.id)
      .time(slot.startTime)
      .duration(meetingType.durationMinutes)
      .timezone(timezone);
  });

  if (coupon) builder.coupon(coupon);
  if (guestContact) builder.guestContact(guestContact);

  return builder.url();
}
